<template>
    <div class="ai-md" @click="onClick">
        <template v-for="(segment, index) in segments">
            <!-- eslint-disable-next-line vue/no-v-html -- HTML-escaped then whitelisted by renderMarkdown -->
            <div
                v-if="segment.kind === 'markdown'"
                :key="`md-${index}`"
                v-html="segment.html"
            ></div>
            <AiChatChart
                v-else-if="segment.kind === 'chart'"
                :key="`chart-${index}`"
                :spec="segment.spec"
            />
            <AiChatStats v-else :key="`stats-${index}`" :spec="segment.spec" />
        </template>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import {
    INTERNAL_LINK_ATTRIBUTE,
    parseAssistantContent,
} from "@/Pages/AiChat/markdown";
import type { AiLinkHosts } from "@/Pages/AiChat/markdown";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const props = defineProps<{
    content: string;
}>();

const NO_HOSTS: AiLinkHosts = { app: null, trusted: [] };

/**
 * The server decides which hosts may become a clickable link; an absent list
 * means no link is rendered at all, which is the safe state.
 */
const linkHosts = computed<AiLinkHosts>(
    () => usePage<IRhizomPageProps>().props.aiChatLinkHosts ?? NO_HOSTS,
);

const segments = computed(() =>
    parseAssistantContent(props.content, linkHosts.value),
);

/**
 * A link to a page of the application navigates inside the SPA, the way the
 * assistant's "go to page" button does, instead of reloading the whole app.
 * A modified click (new tab, new window, download) is left to the browser.
 */
const onClick = (event: MouseEvent): void => {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return;
    }

    const target = event.target as HTMLElement | null;
    const anchor = target?.closest<HTMLAnchorElement>(
        `a[${INTERNAL_LINK_ATTRIBUTE}]`,
    );

    if (!anchor) {
        return;
    }

    event.preventDefault();

    const destination = new URL(anchor.href);

    router.visit(
        `${destination.pathname}${destination.search}${destination.hash}`,
    );
};
</script>

<style scoped>
.ai-md {
    font-size: 0.875rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.ai-md :deep(p) {
    margin: 0 0 0.5rem;
}

.ai-md :deep(p:last-child) {
    margin-bottom: 0;
}

.ai-md :deep(h3),
.ai-md :deep(h4) {
    font-weight: 600;
    margin: 0.75rem 0 0.35rem;
    color: var(--color-gray-800);
}

.ai-md :deep(ul),
.ai-md :deep(ol) {
    margin: 0.25rem 0 0.5rem;
    padding-left: 1.25rem;
}

.ai-md :deep(ul) {
    list-style: disc;
}

.ai-md :deep(ol) {
    list-style: decimal;
}

.ai-md :deep(li) {
    margin: 0.1rem 0;
}

.ai-md :deep(strong) {
    font-weight: 600;
}

/* Outbound links. Same pill as the in-app "Go to page" button so the chat keeps
   one visual language for "here is where to continue", but in blue with the
   external-link icon: a different colour, hover and glyph make it obvious the
   jump leaves the application and opens in a new tab. */
.ai-md :deep(a.ai-md-link) {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    max-width: 100%;
    vertical-align: bottom;
    border-radius: 9999px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    padding: 0.05rem 0.5rem;
    color: #1d4ed8;
    font-weight: 500;
    text-decoration: none;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;
}

.ai-md :deep(a.ai-md-link:hover) {
    border-color: #93c5fd;
    background: #dbeafe;
    color: #1e3a8a;
}

.ai-md :deep(a.ai-md-link:focus-visible) {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
}

.ai-md :deep(.ai-md-link-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ai-md :deep(.ai-md-link-icon) {
    width: 0.85em;
    height: 0.85em;
    flex-shrink: 0;
    opacity: 0.75;
}

.ai-md :deep(a.ai-md-link:hover .ai-md-link-icon) {
    opacity: 1;
}

/* A link that stays inside the application wears the same orange as the
   assistant's "Go to page" button and its arrow glyph, so colour alone answers
   "does this take me out of the app?". */
.ai-md :deep(a.ai-md-link-internal) {
    border-color: #ffd5cc;
    background: #fff1ee;
    color: #c9330f;
}

.ai-md :deep(a.ai-md-link-internal:hover) {
    border-color: #ffb3a3;
    background: #ffe1db;
    color: #a32a0c;
}

.ai-md :deep(a.ai-md-link-internal:focus-visible) {
    outline-color: #ff421e;
}

/* A link on a line of its own is the reply's call to action, so it gets the full
   button treatment — same shape and size as the orange "Go to page" button. */
.ai-md :deep(.ai-md-link-row) {
    display: flex;
    margin: 0.5rem 0;
}

.ai-md :deep(a.ai-md-link-block) {
    gap: 0.375rem;
    border-color: transparent;
    background: #2563eb;
    padding: 0.375rem 0.75rem;
    color: #fff;
    font-size: 0.75rem;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
    transition:
        background-color 0.15s ease,
        transform 0.1s ease;
}

.ai-md :deep(a.ai-md-link-block:hover) {
    border-color: transparent;
    background: #1d4ed8;
    color: #fff;
}

.ai-md :deep(a.ai-md-link-block:active) {
    transform: scale(0.95);
}

.ai-md :deep(a.ai-md-link-block.ai-md-link-internal) {
    background: #ff421e;
}

.ai-md :deep(a.ai-md-link-block.ai-md-link-internal:hover) {
    background: #e03a1a;
    color: #fff;
}

.ai-md :deep(a.ai-md-link-block .ai-md-link-icon) {
    width: 1rem;
    height: 1rem;
    opacity: 1;
}

.ai-md :deep(code) {
    font-family:
        ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono",
        monospace;
    font-size: 0.8em;
    background: var(--color-gray-100);
    border-radius: 4px;
    padding: 0.1em 0.35em;
    word-break: break-all;
}

.ai-md :deep(pre) {
    background: var(--color-gray-100);
    border-radius: 8px;
    padding: 0.75rem;
    margin: 0.5rem 0;
    overflow-x: auto;
}

.ai-md :deep(pre code) {
    background: transparent;
    padding: 0;
    white-space: pre-wrap;
    word-break: break-word;
}

.ai-md :deep(.ai-md-table) {
    margin: 0.5rem 0;
    max-width: 100%;
    overflow-x: auto;
}

.ai-md :deep(table) {
    width: 100%;
    min-width: 26rem;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.ai-md :deep(th),
.ai-md :deep(td) {
    border: 1px solid var(--color-gray-200);
    padding: 0.35rem 0.5rem;
    text-align: left;
    vertical-align: top;
}

.ai-md :deep(th) {
    background: var(--color-gray-100);
    font-weight: 600;
    color: var(--color-gray-700);
}

/* Narrow chat surfaces (mini widget, squeezed big chat) are marked as CSS
   containers; inside them tables give up their desktop minimum width and wrap
   cell content instead of forcing a horizontal scroll. Pages without a
   container ancestor keep the desktop table layout untouched. */
@container (max-width: 32rem) {
    .ai-md :deep(table) {
        min-width: 0;
        font-size: 0.75rem;
    }

    .ai-md :deep(th),
    .ai-md :deep(td) {
        padding: 0.25rem 0.4rem;
        overflow-wrap: anywhere;
    }
}
</style>
